import React, { memo, useCallback, useMemo, useState, useEffect, useRef } from 'react';

import {
  calculateDailyAverage,
  calculateWeeklyAverage,
  generateDateRange,
  generateWeekRange,
} from '../../../../admin/src/utils/functions';
import LineChart from './LineChart';
import CustomTooltip from './CustomTooltip';
import { MarketData } from 'common/src/utils/types';
import { BitacoraPriceData } from 'common/src/api/market_comments';
import ChineseChart from './ChineseChart';
import SeasonWeekChart from './SeasonWeekChart';
import useDataStore from '../../stores/useDataStore';
import ChineseTooltip from './ChineseTooltip';
import useUserStore from '../../stores/useUserStore';
import {
  BitacoraMode,
  appendBitacoraDetails,
  buildBitacoraSeries,
} from './bitacoraSeries';
import { useTranslation } from '../../hooks/useTranslation';
import EvolutionChartFilters from './EvolutionChartFilters';
import ChartDownloadButton from './ChartDownloadButton';
import { MARKETS, MARKET_LABELS } from '../../utils/definitions';

interface CaliberEvolutionChartProps {
  data: MarketData[];
  selectedCurrency: 'RMB' | 'USD';
  rangeSelected: { startDate: string; endDate: string };
  priceView: 'kg' | 'box';
  showPastsSeasons: boolean;
  showExporterPrices: boolean;
  viewMode: 'chinese' | 'normal';
  allMarketData?: MarketData[]; // Optional: processed allMarketData from parent
  visualization?: 'chart' | 'table';
  chartViewMode?: 'day' | 'week';
  bitacoraData?: BitacoraPriceData[]; // Bitácora prices to overlay on chart
  bitacoraMode?: BitacoraMode; // Un punto promedio, o uno por cliente o por etiqueta
}

const CaliberEvolutionChart: React.FC<CaliberEvolutionChartProps> = React.memo(
  ({
    data,
    selectedCurrency,
    rangeSelected,
    priceView,
    showPastsSeasons,
    showExporterPrices,
    viewMode,
    allMarketData: allMarketDataProp,
    visualization = 'chart',
    chartViewMode = 'day',
    bitacoraData = [],
    bitacoraMode = 'average',
  }) => {
    const { t } = useTranslation('market');
    const { allMarketData: allMarketDataStore, selectedSpecies } = useDataStore();
    // Use prop if provided, otherwise fallback to store
    const allMarketData = allMarketDataProp || allMarketDataStore;
    const { userProfile, darkMode } = useUserStore();
    const ownExporter = userProfile?.exporter;

    // Memoize static lists and default values
    // When showing past seasons, build lists from allMarketData to include all calibers/varieties/formats
    const staticData = useMemo(() => {
      const dataSource = showPastsSeasons ? allMarketData : data;

      // Count occurrences of each caliber
      const caliberCounts: Record<string, number> = {};
      dataSource.forEach((item) => {
        if (item.size) {
          caliberCounts[item.size] = (caliberCounts[item.size] || 0) + 1;
        }
      });

      // Sort calibers by count and take top 3
      const topCalibers = Object.entries(caliberCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([caliber]) => caliber);

      // Default to top calibers if available, otherwise fall back to J, 2J, 3J
      const defaultCalibers =
        topCalibers.length > 0
          ? topCalibers
          : Object.keys(caliberCounts).filter((c) => ['J', '2J', '3J'].includes(c)).slice(0, 3);

      // Todas las variedades, ordenadas por cantidad de registros
      const varietyCounts: Record<string, number> = {};
      dataSource.forEach((item) => {
        if (item.variety) {
          varietyCounts[item.variety] = (varietyCounts[item.variety] || 0) + 1;
        }
      });
      const defaultVarieties = Object.entries(varietyCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([variety]) => variety);

      // Todos los formatos, ordenados por cantidad de registros
      const formatCounts: Record<string, number> = {};
      dataSource.forEach((item) => {
        if (item.weight_kg) {
          formatCounts[item.weight_kg] = (formatCounts[item.weight_kg] || 0) + 1;
        }
      });
      const defaultFormats = Object.entries(formatCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([format]) => format);

      // Get all markets (using MARKETS constant like MarketsEvolutionChart)
      const defaultMarkets = MARKETS.filter((m) => m !== 'all');

      // Mismos códigos que lista ColorFilter (D, L…), en el mismo orden.
      const colorSet = new Set<string>();
      dataSource.forEach((item) => {
        colorSet.add(item.color || 'Sin Color');
      });
      const defaultColors = Array.from(colorSet).sort((a, b) => a.localeCompare(b));

      // Get all fleets (using codes, not translations)
      const fleetSet = new Set<string>();
      dataSource.forEach((item) => {
        if (item.transport === 'AIR') {
          fleetSet.add('AIR');
        } else if (item.transport === 'OCEAN') {
          fleetSet.add('OCEAN');
        }
      });
      const defaultFleets = Array.from(fleetSet);

      return {
        defaultCalibers,
        defaultVarieties,
        defaultFormats,
        defaultMarkets,
        defaultColors,
        defaultFleets,
      };
    }, [data, showPastsSeasons, allMarketData]);

    // States - Initialize with default values (all as arrays for multiple selection)
    const [selectedCalibers, setSelectedCalibers] = useState<string[]>(
      staticData.defaultCalibers
    );
    const [selectedVarieties, setSelectedVarieties] = useState<string[]>(
      staticData.defaultVarieties
    );
    const [selectedFormats, setSelectedFormats] = useState<string[]>(
      staticData.defaultFormats
    );
    const [selectedMarkets, setSelectedMarkets] = useState<string[]>(
      staticData.defaultMarkets
    );
    const [selectedColors, setSelectedColors] = useState<string[]>(
      staticData.defaultColors
    );
    const [selectedFleets, setSelectedFleets] = useState<string[]>(
      staticData.defaultFleets
    );
    const [totalRecordsFiltered, setTotalRecordsFiltered] = useState(0);

    // Track if initial selections have been set
    const initialSelectionsSet = useRef(false);
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const previousSpeciesId = useRef(selectedSpecies?.id);

    // Update selections when species changes
    useEffect(() => {
      const speciesChanged = previousSpeciesId.current !== selectedSpecies?.id;

      if (speciesChanged && initialSelectionsSet.current) {
        setSelectedCalibers(staticData.defaultCalibers);
        setSelectedVarieties(staticData.defaultVarieties);
        setSelectedFormats(staticData.defaultFormats);
        setSelectedMarkets(staticData.defaultMarkets);
        setSelectedColors(staticData.defaultColors);
        setSelectedFleets(staticData.defaultFleets);

        previousSpeciesId.current = selectedSpecies?.id;
      } else if (!initialSelectionsSet.current) {
        initialSelectionsSet.current = true;
      }
    }, [
      selectedSpecies?.id,
      staticData.defaultCalibers,
      staticData.defaultVarieties,
      staticData.defaultFormats,
      staticData.defaultMarkets,
      staticData.defaultColors,
      staticData.defaultFleets,
    ]);

    // Memoize filter function - All filters are now arrays (multiple selection)
    const filterData = useCallback(
      (marketData: MarketData[]) => {
        return marketData.filter((item) => {
          // Caliber filter
          if (selectedCalibers.length > 0 && !selectedCalibers.includes(item.size)) {
            return false;
          }

          // Variety filter
          if (selectedVarieties.length > 0 && !selectedVarieties.includes(item.variety)) {
            return false;
          }

          // Format filter
          if (selectedFormats.length > 0 && !selectedFormats.includes(item.weight_kg)) {
            return false;
          }

          // Market filter (using region field, matching MarketsEvolutionChart logic)
          if (selectedMarkets.length > 0) {
            const itemRegion = item.region?.toUpperCase();
            if (!selectedMarkets.includes(itemRegion)) {
              return false;
            }
          }

          // Color filter
          if (selectedColors.length > 0) {
            const itemColor = item.color || 'Sin Color';
            if (!selectedColors.includes(itemColor)) {
              return false;
            }
          }

          // Fleet filter
          if (selectedFleets.length > 0) {
            if (!selectedFleets.includes(item.transport)) {
              return false;
            }
          }

          return true;
        });
      },
      [selectedCalibers, selectedVarieties, selectedFormats, selectedMarkets, selectedColors, selectedFleets]
    );

    // Memoize chart data preparation
    const prepareChartData = useCallback(
      (
        marketData: MarketData[],
        dateRange?: { startDate: string; endDate: string },
        viewMode: 'day' | 'week' = 'day'
      ) => {
        const filteredData = filterData(marketData);
        let totalRecords = 0;

        // Get the full date/week range for alignment
        const allDates = dateRange
          ? viewMode === 'day'
            ? generateDateRange(dateRange.startDate, dateRange.endDate)
            : generateWeekRange(dateRange.startDate, dateRange.endDate)
          : [];

        // Series regulares
        const regularSeries = selectedCalibers.map((caliber, index) => {
          const caliberData = filteredData.filter(
            (item) => item.size === caliber
          );
          totalRecords += caliberData.length;

          const dailyAverages =
            viewMode === 'day'
              ? calculateDailyAverage(caliberData)
              : calculateWeeklyAverage(caliberData);

          // Create a map of date/week to value
          const dataMap = new Map<string, { y: number; count: number }>();
          dailyAverages.forEach((avg) => {
            if (viewMode === 'day') {
              const date = new Date(avg.x);
              const year = date.getUTCFullYear();
              const month = String(date.getUTCMonth() + 1).padStart(2, '0');
              const day = String(date.getUTCDate()).padStart(2, '0');
              const dateStr = `${year}-${month}-${day}`;
              dataMap.set(dateStr, { y: avg.y, count: (avg as any).count || 1 });
            } else {
              // For weekly view, avg.x is already "W{number}"
              dataMap.set(String(avg.x), { y: avg.y, count: (avg as any).count || 1 });
            }
          });

          // Map to full date range - IMPORTANT: include null for missing dates
          let alignedData: { x: string; y: number | null; count?: number }[];
          if (allDates.length > 0) {
            // When we have a date range, use it and map data to it
            alignedData = allDates.map(dateStr => {
              const point = dataMap.get(dateStr);
              return {
                x: dateStr,
                y: point?.y ?? null,
                count: point?.count
              };
            });
          } else if (dataMap.size > 0) {
            // No date range but we have data, use the data dates
            alignedData = Array.from(dataMap).map(([dateStr, point]) => ({
              x: dateStr,
              y: point.y,
              count: point.count
            }));
          } else {
            // No data at all - return empty array
            alignedData = [];
          }

          return {
            name: caliber,
            data: alignedData,
            color: `hsl(${index * (360 / selectedCalibers.length)}, 70%, 50%)`,
            type: 'line',
          };
        });

        // Series de la exportadora del usuario
        const exporterSeries = selectedCalibers.map((caliber, index) => {
          const caliberData = filteredData.filter(
            (item) =>
              item.size === caliber && item.exporter === ownExporter?.excel_name
          );

          // Calcular promedios diarios/semanales de la exportadora
          const exporterDailyAverages =
            viewMode === 'day'
              ? calculateDailyAverage(caliberData)
              : calculateWeeklyAverage(caliberData);

          // Create a map of date to value
          const exporterDataMap = new Map<string, { y: number; count: number }>();
          exporterDailyAverages.forEach((avg) => {
            if (viewMode === 'day') {
              const date = new Date(avg.x);
              const year = date.getUTCFullYear();
              const month = String(date.getUTCMonth() + 1).padStart(2, '0');
              const day = String(date.getUTCDate()).padStart(2, '0');
              const dateStr = `${year}-${month}-${day}`;
              exporterDataMap.set(dateStr, { y: avg.y, count: (avg as any).count || 1 });
            } else {
              // For weekly view, avg.x is already "W{number}"
              exporterDataMap.set(String(avg.x), { y: avg.y, count: (avg as any).count || 1 });
            }
          });

          // Map to full date range - same as regular series
          let alignedData: { x: string; y: number | null; count?: number }[];
          if (allDates.length > 0) {
            // When we have a date range, use it and map data to it
            alignedData = allDates.map(dateStr => {
              const point = exporterDataMap.get(dateStr);
              return {
                x: dateStr,
                y: point?.y ?? null,
                count: point?.count
              };
            });
          } else if (exporterDataMap.size > 0) {
            // No date range but we have data, use the data dates
            alignedData = Array.from(exporterDataMap).map(([dateStr, point]) => ({
              x: dateStr,
              y: point.y,
              count: point.count
            }));
          } else {
            // No data at all - return empty array
            alignedData = [];
          }

          return {
            name: `${caliber} ${ownExporter?.excel_name || 'Exportadora'}`,
            data: alignedData, // Now aligned with full date range
            color: `hsl(${index * (360 / selectedCalibers.length)}, 70%, ${showPastsSeasons ? 30 : 80
              }%)`,
            isExporter: true,
            type: 'line',
            markers: {
              size: 6,
              shape: 'diamond',
              strokeWidth: 2,
              hover: {
                size: 8,
              },
            },
          };
        });

        setTotalRecordsFiltered(totalRecords);

        // Puntos de Bitácora: promedio ponderado por bultos, o uno por cliente o por etiqueta.
        const bitacoraSeries = buildBitacoraSeries({
          records: bitacoraData,
          categories: allDates,
          categoryOf: (b) =>
            b.caliber && selectedCalibers.some(c => c.toUpperCase() === b.caliber?.toUpperCase())
              ? b.date_from
              : null,
          mode: bitacoraMode,
          label: t('Bitácora'),
          noClientLabel: t('Sin cliente'),
          noLabelLabel: t('Sin etiqueta o varias'),
        }).map((series) => ({ ...series, isBitacora: true }));

        return {
          series: showExporterPrices
            ? [...regularSeries, ...exporterSeries, ...bitacoraSeries]
            : [...regularSeries, ...bitacoraSeries],
          categories: Array.from(
            new Set(filteredData.map((item) => item.date))
          ).sort(),
        };
      },
      [
        filterData,
        selectedCalibers,
        ownExporter?.excel_name,
        showExporterPrices,
        showPastsSeasons,
        bitacoraData,
        bitacoraMode,
      ]
    );

    const chartData = useMemo(
      () => {
        return prepareChartData(
          showPastsSeasons ? allMarketData : data,
          !showPastsSeasons ? rangeSelected : undefined,
          chartViewMode
        );
      },
      [
        prepareChartData,
        showPastsSeasons,
        allMarketData,
        data,
        rangeSelected,
        chartViewMode,
      ]
    );

    const tooltipCustom = ({
      seriesIndex,
      dataPointIndex,
      w,
    }: {
      seriesIndex: number;
      dataPointIndex: number;
      w: any;
    }): string => {
      // Obtenemos la fecha del punto actual (ahora es un string)
      const currentSeries = w.globals.initialSeries[seriesIndex];
      const currentPoint = currentSeries.data[dataPointIndex];

      if (!currentPoint) return '';

      // Tooltip compartido: al final se agrega el detalle de los registros de
      // Bitácora de esa fecha (cliente, comentario, foto, quién lo registró).
      const withBitacora = (html: string) =>
        appendBitacoraDetails(html, {
          w,
          x: currentPoint.x,
          currency: selectedCurrency,
          unit: priceView === 'kg' ? '/kg' : '/5kg box',
          darkMode,
          t,
        });



      if (viewMode === 'chinese') {
        return withBitacora(ChineseTooltip({
          seriesIndex,
          dataPointIndex,
          w,
          selectedCurrency,
          priceView,
        }) as string);
      }

      return withBitacora(CustomTooltip({
        seriesIndex,
        dataPointIndex,
        w,
        selectedCurrency,
        chartType: 'caliber',
        priceView: priceView,
      }) as string);
    };

    // Table View Component
    const TableView = () => {
      // Prepare table data - dates as columns, series as rows
      const tableData = useMemo(() => {
        // Get all unique dates sorted
        const allDates = new Set<string>();
        chartData.series.forEach((serie) => {
          serie.data.forEach((point) => {
            allDates.add(point.x);
          });
        });
        const sortedDates = Array.from(allDates).sort();

        // Build data structure: each row is a series, each column is a date
        const rows = chartData.series.map((serie) => {
          const dateMap = new Map(
            serie.data.map((point) => [point.x, point.y])
          );
          return {
            serieName: serie.name,
            values: sortedDates.map((date) => dateMap.get(date) ?? null),
          };
        });

        return { dates: sortedDates, rows };
      }, [chartData.series]);

      return (
        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-default-200'>
            <thead className='bg-default-100 sticky top-0'>
              <tr>
                <th className='px-6 py-3 text-left text-xs font-medium text-default-500 uppercase tracking-wider sticky left-0 bg-default-100 z-10'>
                  {t('Serie')}
                </th>
                {tableData.dates.map((dateStr) => (
                  <th
                    key={dateStr}
                    className='px-6 py-3 text-left text-xs font-medium text-default-500 uppercase tracking-wider'
                  >
                    {new Date(dateStr).toLocaleDateString()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className='bg-background divide-y divide-default-200'>
              {tableData.rows.map((row, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-background' : 'bg-default-100'}>
                  <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-default-900 sticky left-0 bg-inherit'>
                    {row.serieName}
                  </td>
                  {row.values.map((value, dateIdx) => (
                    <td
                      key={dateIdx}
                      className='px-6 py-4 whitespace-nowrap text-sm text-default-500'
                    >
                      {value != null
                        ? `${selectedCurrency} ${value.toFixed(2)}`
                        : '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    };

    return (
      <div>
        <div className='flex mb-4'>
          <div className='flex gap-4 flex-col w-full'>
            <div className='flex flex-col sm:flex-row justify-between items-center'>
              <div className='flex items-center gap-2'>
                <h3 className='text-lg font-bold'>{t('Por calibre')}</h3>
                {visualization !== 'table' && (
                  <ChartDownloadButton
                    chartRef={chartContainerRef}
                    title={t('Por calibre')}
                    subtitle={`${totalRecordsFiltered} ${t('registros totales')}`}
                  />
                )}
              </div>
              <p className='text-sm text-default-500'>
                {totalRecordsFiltered} {t('registros totales')}
              </p>
            </div>
            <EvolutionChartFilters
              primaryFilter="caliber"
              data={showPastsSeasons ? allMarketData : data}
              selectedCalibers={selectedCalibers}
              onCalibersChange={setSelectedCalibers}
              selectedVarieties={selectedVarieties}
              onVarietiesChange={(values) => setSelectedVarieties(values)}
              selectedFormats={selectedFormats}
              onFormatsChange={setSelectedFormats}
              selectedMarkets={selectedMarkets}
              onMarketsChange={setSelectedMarkets}
              selectedColors={selectedColors}
              onColorsChange={setSelectedColors}
              selectedFleets={selectedFleets}
              onFleetsChange={setSelectedFleets}
              hasColor={selectedSpecies?.has_color || false}
            />
          </div>
        </div>
        {visualization === 'table' ? (
          <TableView />
        ) : (
          <div ref={chartContainerRef}>
            {viewMode === 'chinese' ? (
              <ChineseChart
            series={chartData.series}
            xaxisCategories={chartData.categories.map((c) => String(c))}
            height={350}
            priceView={priceView}
            yaxisTitle={`Precio ${selectedCurrency}/${priceView === 'kg' ? 'Kg' : '5kg box'
              }`}
            tooltipCustom={tooltipCustom}
            selectedCurrency={selectedCurrency}
          />
        ) : showPastsSeasons ? (
          <SeasonWeekChart
            series={chartData.series}
            xaxisCategories={chartData.categories.map((c) => String(c))}
            priceView={priceView}
            height={350}
            yaxisTitle={`Precio ${selectedCurrency}/${priceView === 'kg' ? 'Kg' : '5kg box'
              }`}
            tooltipCustom={tooltipCustom}
            selectedCurrency={selectedCurrency}
          />
        ) : (
          <LineChart
            series={chartData.series}
            xaxisCategories={
              chartViewMode === 'day'
                ? generateDateRange(
                  rangeSelected.startDate,
                  rangeSelected.endDate
                )
                : generateWeekRange(
                  rangeSelected.startDate,
                  rangeSelected.endDate
                )
            }
            yaxisTitle={`Precio ${selectedCurrency} / ${priceView === 'kg' ? 'Kg' : '5kg box'
              }`}
            tooltipCustom={tooltipCustom}
            selectedCurrency={selectedCurrency}
            priceView={priceView}
            showExporterPrices={showExporterPrices}
          />
        )}
          </div>
        )}
      </div>
    );
  }
);

export default memo(CaliberEvolutionChart);
